import React, { useState, useEffect, useCallback, useContext, memo } from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonIcon,
  IonButton,
  IonBadge,
  IonSpinner,
  useIonRouter,
} from '@ionic/react';
import {
  person,
  settings,
  notifications,
  statsChartOutline,
  peopleOutline,
  trendingUp,
  chatbubblesOutline,
  barChartOutline,
  personAdd,
  calendar,
  people,
  clipboard,
  cash,
  receipt,
  calculator,
  construct,
  speedometer,
  pencil,
  cloudDownload,
  peopleCircle,
  barChart,
  notificationsOutline,
  call,
  arrowUpOutline,
  arrowDownOutline,
  ellipsisHorizontal,
  filterOutline,
  refreshOutline,
  addOutline,
  chevronForwardOutline,
  closeOutline,
  checkmarkOutline,
} from 'ionicons/icons';
import { useAuth } from '../../../../contexts';
import { AuthContext } from '../../../../contexts/auth-context-core';
import { getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';
import { Shortcut } from '@rsmaids/shared/types/dashboard';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { fetchLeadsFromApi } from '../../../../services/glsaLeadsService';
import type { GlsaLeadDoc } from '../../../../services/glsaLeadsService';
import { getBookingsFromCache } from '@rsmaids/shared/services/api/bookingsCacheService';
import type { BookingItem } from '@rsmaids/shared/types/bookings';
import { getServiceOrder } from '../../../../services/zohoServiceOrderService';
import { getNotificationsViaFunction } from '../../../../utils/notificationUtils';
import { openIvrDialer } from '../../../components/IvrPanel/ivrPanelBus';
import styles from './salesDashboardDesktop.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import { intlLocale } from '../../../../i18n/locale';

function formatLeadTime(iso: string | null): string {
  if (!iso) return i18n.t('desktop.pages.dashboard.sales.DashboardSalesDesktop.recently');
  const d = new Date(iso);
  if (isNaN(d.getTime())) return i18n.t('desktop.pages.dashboard.sales.DashboardSalesDesktop.recently');
  const ms = Date.now() - d.getTime();
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);
  if (mins < 2) return i18n.t('desktop.pages.dashboard.sales.DashboardSalesDesktop.justNow');
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return i18n.t('desktop.pages.dashboard.sales.DashboardSalesDesktop.yesterday');
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' });
}

function leadStatusColor(status: string): string {
  switch (status) {
    case 'NEW': return 'warning';
    case 'ACTIVE': return 'primary';
    case 'BOOKED': return 'success';
    case 'DECLINED': return 'danger';
    default: return 'medium';
  }
}

function leadStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    NEW: i18n.t('desktop.pages.dashboard.sales.DashboardSalesDesktop.new'), ACTIVE: i18n.t('desktop.pages.dashboard.sales.DashboardSalesDesktop.active'), BOOKED: i18n.t('desktop.pages.dashboard.sales.DashboardSalesDesktop.booked'),
    DECLINED: i18n.t('desktop.pages.dashboard.sales.DashboardSalesDesktop.declined'), EXPIRED: i18n.t('desktop.pages.dashboard.sales.DashboardSalesDesktop.expired'), DISABLED: i18n.t('desktop.pages.dashboard.sales.DashboardSalesDesktop.disabled'),
  };
  return labels[status] ?? status;
}

function bookingStage(status: BookingItem['status']): string {
  const stages: Record<string, string> = {
    pending: 'Pending', confirmed: 'Confirmed', completed: 'Completed',
    cancelled: 'Cancelled', due: 'Payment Due', overdue: 'Overdue',
  };
  return stages[status] ?? status;
}

function bookingPriority(status: BookingItem['status']): { label: string; color: string } {
  switch (status) {
    case 'confirmed': return { label: 'Active', color: 'success' };
    case 'completed': return { label: 'Done', color: 'success' };
    case 'cancelled': return { label: 'Cancelled', color: 'danger' };
    case 'due': return { label: 'Due', color: 'warning' };
    case 'overdue': return { label: 'Overdue', color: 'danger' };
    default: return { label: 'New', color: 'warning' };
  }
}

function abbreviateName(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0] || name;
}

function formatPrice(amount: number | undefined): string {
  if (!amount) return '—';
  return amount >= 1000 ? `$${(amount / 1000).toFixed(1)}K` : `$${Math.round(amount)}`;
}

const DashboardSalesDesktop: React.FC = () => {
  const { t: tr } = useTranslation();
  const router = useIonRouter();
  const { authState } = useAuth();
  const { user } = authState;
  const authContext = useContext(AuthContext);

  const [isEditMode, setIsEditMode] = useState(false);
  const [draftShortcutIds, setDraftShortcutIds] = useState<string[]>([]);
  const [isSavingShortcuts, setIsSavingShortcuts] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [realLeads, setRealLeads] = useState<GlsaLeadDoc[]>([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(true);
  const [realPipeline, setRealPipeline] = useState<BookingItem[]>([]);
  const [pipelinePriceMap, setPipelinePriceMap] = useState<Record<string, number>>({});
  const [pipelineTotalValue, setPipelineTotalValue] = useState<number | null>(null);

  const [activeShortcutIds, setActiveShortcutIds] = useState<string[]>(
    user?.preferences?.dashboardShortcuts || ['leads', 'analytics', 'profile', 'alerts', 'settings']
  );

  useEffect(() => {
    const load = async () => {
      if (!user?.id) return;
      try {
        const result = await getNotificationsViaFunction(user.id);
        const notifs = (result?.notifications || []) as Array<{ read?: boolean }>;
        setUnreadNotificationsCount(notifs.filter(n => !n.read).length);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user?.id]);

  // Computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as setState calls inside an effect,
  // since this is a pure derivation from user/localStorage with no
  // external system being subscribed to.
  const [prevUserForShortcuts, setPrevUserForShortcuts] = useState(user);
  if (prevUserForShortcuts !== user) {
    setPrevUserForShortcuts(user);
    if (user) {
      if (user.preferences?.dashboardShortcuts?.length) {
        setActiveShortcutIds(user.preferences.dashboardShortcuts);
      } else {
        const local = localStorage.getItem('dashboard-sales-shortcuts');
        let usedLocal = false;
        if (local) {
          try {
            const parsed = JSON.parse(local);
            if (Array.isArray(parsed) && parsed.length > 0) { setActiveShortcutIds(parsed); usedLocal = true; }
          } catch { /* ignore */ }
        }
        if (!usedLocal) setActiveShortcutIds(['leads', 'analytics', 'profile', 'alerts', 'settings']);
      }
    }
  }

  const loadLeads = useCallback(async () => {
    setIsLoadingLeads(true);
    try {
      const leads = await fetchLeadsFromApi(10);
      setRealLeads(leads.slice(0, 5));
    } catch { /* silent */ } finally {
      setIsLoadingLeads(false);
    }
  }, []);

  useEffect(() => {
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body).
    Promise.resolve().then(() => loadLeads());
  }, [loadLeads]);

  useEffect(() => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 30);
    getBookingsFromCache(start, end).then(async bookings => {
      const active = bookings
        .filter(b => b.status !== 'cancelled' && b.status !== 'completed')
        .sort((a, b) => {
          const at = a.startTime instanceof Date ? a.startTime.getTime() : new Date(a.startTime).getTime();
          const bt = b.startTime instanceof Date ? b.startTime.getTime() : new Date(b.startTime).getTime();
          return at - bt;
        })
        .slice(0, 5);
      setRealPipeline(active);

      // Fetch Zoho SO prices in parallel for each pipeline booking
      const soResults = await Promise.allSettled(
        active.map(b => getServiceOrder(b.id))
      );
      const priceMap: Record<string, number> = {};
      let total = 0;
      soResults.forEach((result, i) => {
        if (result.status === 'fulfilled' && result.value?.total) {
          priceMap[active[i].id] = result.value.total;
          total += result.value.total;
        }
      });
      setPipelinePriceMap(priceMap);
      setPipelineTotalValue(total);
    }).catch(() => {});
  }, []);

  const saveShortcutsToStorage = useCallback(async (shortcuts: string[]) => {
    try {
      await DataServiceFactory.getService().auth.updateUserPreferences({ dashboardShortcuts: shortcuts });
      authContext?.actions?.updateLocalPreferences({ dashboardShortcuts: shortcuts });
      localStorage.setItem('dashboard-sales-shortcuts', JSON.stringify(shortcuts));
    } catch {
      localStorage.setItem('dashboard-sales-shortcuts', JSON.stringify(shortcuts));
    }
  }, [authContext]);

  const openShortcutEditor = useCallback(() => {
    setDraftShortcutIds(activeShortcutIds);
    setIsEditMode(true);
  }, [activeShortcutIds]);

  const closeShortcutEditor = useCallback(() => {
    setIsEditMode(false);
    setDraftShortcutIds([]);
  }, []);

  const toggleDraftShortcut = useCallback((id: string) => {
    setDraftShortcutIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }, []);

  const saveShortcutEditor = useCallback(async () => {
    setActiveShortcutIds(draftShortcutIds);
    setIsSavingShortcuts(true);
    try {
      await saveShortcutsToStorage(draftShortcutIds);
    } finally {
      setIsSavingShortcuts(false);
    }
    closeShortcutEditor();
  }, [closeShortcutEditor, draftShortcutIds, saveShortcutsToStorage]);

  const allShortcuts: Shortcut[] = [
    { id: 'calendar',  icon: calendar,          title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.schedule'), route: '/tabs/schedule',            category: 'core' },
    { id: 'contacts',  icon: people,             title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.contacts'), route: '/tabs/contacts',            category: 'core' },
    { id: 'bookings',  icon: clipboard,          title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.bookings'), route: '/tabs/bookings',            category: 'core' },
    { id: 'leads',     icon: peopleOutline,      title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.leads'),    route: '/tabs/leads',               category: 'core' },
    { id: 'payments',  icon: cash,               title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.payments'), route: '/tabs/payments',            category: 'business' },
    { id: 'invoices',  icon: receipt,            title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.invoices'), route: '/tabs/invoice-list-desktop', category: 'business' },
    { id: 'estimates', icon: calculator,         title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.estimates'),route: '/estimates',                category: 'business' },
    { id: 'services',  icon: construct,          title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.services'), route: '/services',                 category: 'business' },
    { id: 'messages',  icon: chatbubblesOutline, title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.messages'), route: '/team/messages',            category: 'communication' },
    { id: 'alerts',    icon: notifications,      title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.alerts'),   route: '/tabs/notifications',       category: 'communication' },
    { id: 'analytics', icon: statsChartOutline,  title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.analytics'),route: '/tabs/business-analytics',  category: 'analytics' },
    { id: 'reports',   icon: barChart,           title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.reports'),  route: '/tabs/reports',             category: 'analytics' },
    { id: 'profile',   icon: person,             title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.profile'),  route: '/tabs/profile',             category: 'admin' },
    { id: 'settings',  icon: settings,           title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.settings'), route: '/tabs/settings',             category: 'admin' },
    { id: 'team',      icon: peopleCircle,       title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.team'),     route: '/tabs/team-members',       category: 'admin' },
    { id: 'backup',    icon: cloudDownload,      title: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.backup'),   route: '/backup',                   category: 'admin' },
  ];

  const categoryNames = {
    core: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.coreFeatures'), business: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.businessTools'),
    communication: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.communication'), analytics: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.analyticsReports'), admin: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.administration'),
  };

  const activeShortcuts = activeShortcutIds
    .map(id => allShortcuts.find(s => s.id === id))
    .filter((s): s is Shortcut => s !== undefined);

  const categorizedShortcuts = Object.entries(categoryNames)
    .map(([categoryId, categoryLabel]) => ({
      id: categoryId,
      label: categoryLabel,
      shortcuts: allShortcuts.filter(s => s.category === categoryId),
    }))
    .filter(category => category.shortcuts.length > 0);

  const metrics = [
    { icon: personAdd,     label: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.newLead2'),  value: '18',     period: 'This week',    trend: 'up',   change: '+12%' },
    { icon: cash,          label: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.revenue'),    value: '$12.4K', period: 'This month',   trend: 'up',   change: '+8%'  },
    { icon: barChartOutline,label:tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.conversion'), value: '24%',    period: 'This month',   trend: 'down', change: '-2%'  },
    { icon: trendingUp,    label: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.avgDeal'),   value: '$687',   period: 'Per sale',     trend: 'up',   change: '+5%'  },
    { icon: peopleOutline, label: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.pipeline'),   value: '$45.2K', period: 'Total value',  trend: 'up',   change: '+18%' },
    { icon: speedometer,   label: tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.winRate'),   value: '68%',    period: 'This quarter', trend: 'up',   change: '+3%'  },
  ];

  return (
    <IonPage>

      <IonHeader className={styles.header}>
        <IonToolbar className={styles.toolbar}>
          <div className={styles.topBarLeft} slot="start">
            <img src="/maskable_icon_x512.png" alt={tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.rsmaidsLogo')} className={styles.companyLogo} />
            <span className={styles.companyName}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.rSMaidsService')}</span>
          </div>
          <div className={styles.topBarRight} slot="end">
            <IonButton fill="outline" size="small" className={styles.actionButton} onClick={() => openIvrDialer()}>
              <IonIcon icon={call} slot="start" />{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.quickCall')}
            </IonButton>
            <IonButton fill="solid" size="small" className={styles.primaryButton} onClick={() => router.push('/tabs/leads')}>
              <IonIcon icon={addOutline} slot="start" />{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.newLead')}
            </IonButton>
            <div className={styles.notifWrap}>
              <IonButton fill="clear" className={styles.iconButton} onClick={() => router.push('/tabs/notifications')}>
                <div className={styles.iconWrap}>
                  <IonIcon icon={notificationsOutline} />
                </div>
              </IonButton>
              {unreadNotificationsCount > 0 && (
                <span className={styles.notifBadge}>
                  {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                </span>
              )}
            </div>
            <IonButton fill="clear" className={styles.iconButton} onClick={() => router.push('/team/messages')}>
              <IonIcon icon={chatbubblesOutline} />
            </IonButton>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent className={styles.pageContent}>

        {/* ── Body ────────────────────────────────────────────────────── */}
        <div className={styles.body}>

          {/* Quick Access shortcuts — full-width horizontal pill row */}
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <span className={styles.panelTitle}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.quickAccess')}</span>
              <IonButton fill="clear" size="small" className={styles.panelAction}
                onClick={openShortcutEditor}>
                <IonIcon icon={pencil} slot="icon-only" />
              </IonButton>
            </div>
            <div className={styles.shortcutsGrid}>
              <div className={`${styles.shortcutItem} ${styles.shortcutCall}`}
                onClick={() => openIvrDialer()}
                role="button" tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && openIvrDialer()}
              >
                <IonIcon icon={call} className={styles.shortcutIcon} />
                <span className={styles.shortcutLabel}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.call')}</span>
              </div>
              {activeShortcuts.map(s => (
                <div key={s.id} className={styles.shortcutItem}
                  onClick={() => router.push(s.route)}
                  role="button" tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && router.push(s.route)}
                >
                  <IonIcon icon={s.icon} className={styles.shortcutIcon} />
                  <span className={styles.shortcutLabel}>{s.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* KPI analytics strip */}
          <div className={styles.metricsRow}>
            {metrics.map((m, i) => (
              <div key={i} className={styles.metricCard}>
                <div className={styles.metricTop}>
                  <div className={styles.metricIconWrap}>
                    <IonIcon icon={m.icon} className={styles.metricIcon} />
                  </div>
                  <div className={`${styles.trendBadge} ${m.trend === 'up' ? styles.trendUp : styles.trendDown}`}>
                    <IonIcon icon={m.trend === 'up' ? arrowUpOutline : arrowDownOutline} />
                    {m.change}
                  </div>
                </div>
                <div className={styles.metricValue}>{m.value}</div>
                <div className={styles.metricLabel}>{m.label}</div>
                <div className={styles.metricPeriod}>{m.period}</div>
              </div>
            ))}
          </div>

          {/* 2-column grid: leads | pipeline */}
          <div className={styles.mainGrid}>

            {/* Recent Leads */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.recentLeads')}</span>
                <div className={styles.panelActions}>
                  <IonButton fill="clear" size="small" className={styles.panelAction}>
                    <IonIcon icon={filterOutline} slot="icon-only" />
                  </IonButton>
                  <IonButton fill="clear" size="small" className={styles.panelAction} onClick={loadLeads}>
                    <IonIcon icon={refreshOutline} slot="icon-only" />
                  </IonButton>
                </div>
              </div>
              <div className={styles.leadsList}>
                {isLoadingLeads ? (
                  <div style={{ padding: '12px 16px', color: 'var(--ion-color-medium)', fontSize: '0.875rem' }}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.loadingLeads')}</div>
                ) : realLeads.length === 0 ? (
                  <div style={{ padding: '12px 16px', color: 'var(--ion-color-medium)', fontSize: '0.875rem' }}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.noRecentLeads')}</div>
                ) : realLeads.map((lead, i) => (
                  <div key={lead.id || i} className={styles.leadRow}
                    onClick={() => router.push('/tabs/leads')}
                    role="button" tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && router.push('/tabs/leads')}
                  >
                    <div className={styles.leadAvatar}>
                      <img src={getDefaultAvatar(lead.contactName || 'Lead')} alt={lead.contactName || tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.lead')} />
                    </div>
                    <div className={styles.leadInfo}>
                      <span className={styles.leadName}>{lead.contactName || tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.unknownLead')}</span>
                      <span className={styles.leadMeta}>
                        {lead.leadType === 'PHONE_CALL' ? tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.phoneCall') : tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.message')} · {formatLeadTime(lead.createdAt)}
                      </span>
                    </div>
                    <IonBadge color={leadStatusColor(lead.status)} className={styles.leadBadge}>
                      {leadStatusLabel(lead.status)}
                    </IonBadge>
                    <IonIcon icon={chevronForwardOutline} className={styles.leadChevron} />
                  </div>
                ))}
              </div>
              <IonButton fill="clear" expand="block" className={styles.viewAllButton}
                onClick={() => router.push('/tabs/leads')}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.viewAllLeads')}
                <IonIcon icon={chevronForwardOutline} slot="end" />
              </IonButton>
            </div>

            {/* Sales Pipeline */}
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.salesPipeline')}</span>
                <IonButton fill="clear" size="small" className={styles.panelAction}>
                  <IonIcon icon={ellipsisHorizontal} slot="icon-only" />
                </IonButton>
              </div>
              <div className={styles.pipelineTotal}>
                <span className={styles.pipelineTotalLabel}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.totalPipelineValue')}</span>
                <span className={styles.pipelineTotalValue}>
                  {pipelineTotalValue === null ? '—' : formatPrice(pipelineTotalValue)}
                </span>
              </div>
              <div className={styles.pipelineList}>
                {realPipeline.length === 0 ? (
                  <div style={{ padding: '12px 16px', color: 'var(--ion-color-medium)', fontSize: '0.875rem' }}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.noActiveBookings')}</div>
                ) : realPipeline.map((booking, i) => {
                  const priority = bookingPriority(booking.status);
                  return (
                    <div key={booking.id || i} className={styles.pipelineRow}
                      onClick={() => router.push('/tabs/bookings')}
                      role="button" tabIndex={0}
                      onKeyDown={e => e.key === 'Enter' && router.push('/tabs/bookings')}
                    >
                      <div className={styles.pipelineInfo}>
                        <span className={styles.pipelineTitle}>{booking.serviceType || booking.title}</span>
                        <span className={styles.pipelineMeta}>
                          {abbreviateName(booking.contact?.name || 'Client')} · {bookingStage(booking.status)}
                        </span>
                      </div>
                      <div className={styles.pipelineRight}>
                        <span className={styles.pipelineValue}>{formatPrice(pipelinePriceMap[booking.id])}</span>
                        <IonBadge color={priority.color} className={styles.pipelineBadge}>{priority.label}</IonBadge>
                      </div>
                    </div>
                  );
                })}
              </div>
              <IonButton fill="clear" expand="block" className={styles.viewAllButton}
                onClick={() => router.push('/tabs/bookings')}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.viewFullPipeline')}
                <IonIcon icon={chevronForwardOutline} slot="end" />
              </IonButton>
            </div>

          </div>{/* /mainGrid */}
        </div>{/* /body */}

      </IonContent>

      <div
        className={`${styles.shortcutEditorBackdrop} ${isEditMode ? styles.shortcutEditorBackdropOpen : ''}`}
        onClick={closeShortcutEditor}
        aria-hidden={!isEditMode}
      />

      <aside
        className={`${styles.shortcutEditorPanel} ${isEditMode ? styles.shortcutEditorPanelOpen : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.editQuickAccessShortcuts')}
      >
        <div className={styles.shortcutEditorHeader}>
          <div>
            <h3 className={styles.shortcutEditorTitle}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.editQuickAccess')}</h3>
            <p className={styles.shortcutEditorSubtitle}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.chooseWhatAppearsInYourDesktop')}</p>
          </div>
          <IonButton fill="clear" className={styles.shortcutEditorClose} onClick={closeShortcutEditor}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        </div>

        <div className={styles.shortcutEditorBody}>
          {categorizedShortcuts.map((category) => (
            <section key={category.id} className={styles.shortcutEditorSection}>
              <h4 className={styles.shortcutEditorSectionTitle}>{category.label}</h4>
              <div className={styles.shortcutEditorList}>
                {category.shortcuts.map((shortcut) => {
                  const isActive = draftShortcutIds.includes(shortcut.id);
                  return (
                    <button
                      key={shortcut.id}
                      type="button"
                      className={`${styles.shortcutEditorItem} ${isActive ? styles.shortcutEditorItemActive : ''}`}
                      onClick={() => toggleDraftShortcut(shortcut.id)}
                    >
                      <span className={styles.shortcutEditorItemStart}>
                        <IonIcon icon={shortcut.icon} className={styles.shortcutEditorIcon} />
                        <span className={styles.shortcutEditorItemLabel}>{shortcut.title}</span>
                      </span>
                      <span className={`${styles.shortcutEditorCheck} ${isActive ? styles.shortcutEditorCheckOn : ''}`}>
                        {isActive && <IonIcon icon={checkmarkOutline} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className={styles.shortcutEditorFooter}>
          <IonButton fill="outline" className={styles.shortcutEditorCancel} onClick={closeShortcutEditor} disabled={isSavingShortcuts}>{tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.cancel')}
          </IonButton>
          <IonButton fill="solid" className={styles.shortcutEditorSave} onClick={saveShortcutEditor} disabled={isSavingShortcuts}>
            {isSavingShortcuts ? <IonSpinner name="dots" /> : tr('desktop.pages.dashboard.sales.DashboardSalesDesktop.saveChanges')}
          </IonButton>
        </div>
      </aside>

    </IonPage>
  );
};

export default memo(DashboardSalesDesktop);
